import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '../../services/api';
import { useAsync } from '../../hooks/useAsync';
import { useQueryParams } from '../../hooks/useQueryParams';
import { dateTime } from '../../services/format';
import { Alert, Cell, Field, Filters, Input, PageTitle, Pager, Row, Select, Table } from '../../components/ui';
import { CONTACT_ROLES, CONTACT_STATUSES, ContactStatusBadge } from './common';

const PAGE_SIZE = 50;
const EXCERPT = 90;

function excerpt(message: string | null): string {
  if (!message) return '—';
  const line = message.replace(/\s+/g, ' ').trim();
  return line.length > EXCERPT ? `${line.slice(0, EXCERPT)}…` : line;
}

/** The website's contact form (sculpt-api `GET /admin/contact-requests`), newest first. */
export function ContactRequestsList() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { get, page, set, reset, setPage } = useQueryParams();
  const role = get('role');
  const status = get('status');
  const q = get('q');
  const { data, error, loading } = useAsync(
    () => api.contactRequests({ role: role || undefined, status: status || undefined, q: q || undefined, page, page_size: PAGE_SIZE }),
    [role, status, q, page],
  );

  return (
    <div>
      <PageTitle title={t('contact.title')} subtitle={data ? `${t('contact.subtitle')} · ${t('common.count', { count: data.total })}` : t('contact.subtitle')} />
      <Filters onReset={reset}>
        <Field label={t('contact.statusLabel')}>
          <Select value={status} onChange={(e) => set({ status: e.target.value })} data-testid="contact-status-filter">
            <option value="">{t('common.any')}</option>
            {CONTACT_STATUSES.map((s) => (
              <option key={s} value={s}>
                {t(`contact.status.${s}`)}
              </option>
            ))}
          </Select>
        </Field>
        <Field label={t('contact.role')}>
          <Select value={role} onChange={(e) => set({ role: e.target.value })} data-testid="contact-role-filter">
            <option value="">{t('common.any')}</option>
            {CONTACT_ROLES.map((r) => (
              <option key={r} value={r}>
                {t(`contact.roles.${r}`)}
              </option>
            ))}
          </Select>
        </Field>
        <Field label={t('common.search')} className="w-64">
          <Input value={q} onChange={(e) => set({ q: e.target.value })} placeholder={t('contact.searchPlaceholder')} />
        </Field>
      </Filters>
      {error && <Alert>{error}</Alert>}
      <Table
        head={['#', t('contact.receivedAt'), t('contact.name'), t('contact.role'), t('contact.email'), t('contact.message'), t('contact.statusLabel'), t('contact.handledBy')]}
        empty={loading ? t('common.loading') : t('common.empty')}
      >
        {(data?.items ?? []).map((c) => (
          <Row key={c.id} onClick={() => navigate(`/contact-requests/${c.id}`)}>
            <Cell mono>{c.id}</Cell>
            <Cell mono className="whitespace-nowrap">{dateTime(c.created_at, i18n.language)}</Cell>
            <Cell>
              <div className="font-medium text-ink">{c.name}</div>
              {c.company && <div className="text-[11.5px] text-muted">{c.company}</div>}
            </Cell>
            <Cell className="whitespace-nowrap">{t(`contact.roles.${c.role}`, c.role)}</Cell>
            <Cell mono>{c.email}</Cell>
            <Cell className="max-w-[360px] text-muted" title={c.message ?? undefined}>
              {excerpt(c.message)}
            </Cell>
            <Cell>
              <ContactStatusBadge status={c.status} />
            </Cell>
            <Cell mono className="text-muted">{c.handled_by_email ?? '—'}</Cell>
          </Row>
        ))}
      </Table>
      {data && <Pager page={data.page} pageSize={data.page_size} total={data.total} onPage={setPage} />}
    </div>
  );
}
